import { cn } from '@/lib/utils'
import { Controller, useFormContext } from 'react-hook-form'
import Card from './Card'
import Grid from './ui/Grid'
import { Label } from './ui/label'

interface CardRadioOption {
    value: string
    titulo: string
    descricao: string
    disabled?: boolean
}

interface CardRadioFormProps {
    name: string
    options: CardRadioOption[]
    label?: string
    helperText?: string
    disabled?: boolean
    spacing?: number
}

export default function CardRadioForm({
    name,
    options,
    label,
    helperText,
    disabled,
    spacing = 2,
}: CardRadioFormProps) {
    const { control } = useFormContext()

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-4 mb-4">
                    {label && <Label>{label}</Label>}

                    <Grid container spacing={spacing}>
                        {options.map(option => {
                            const selecionado = field.value === option.value
                            const opcaoDesabilitada =
                                disabled || option.disabled

                            return (
                                <Grid
                                    key={option.value}
                                    item
                                    xs={12}
                                    sm={(() => {
                                        if (options.length < 2) return 12
                                        if (options.length % 2 === 0) return 6
                                        return 4
                                    })()}
                                    className="h-full"
                                >
                                    <Card
                                        role="radio"
                                        aria-checked={selecionado}
                                        aria-disabled={opcaoDesabilitada}
                                        className={cn(
                                            'flex-1 cursor-pointer transition-colors duration-200',
                                            'hover:bg-primary/10 select-none',
                                            {
                                                'ring-2 ring-primary shadow-lg':
                                                    selecionado,
                                                'opacity-50 cursor-not-allowed':
                                                    opcaoDesabilitada,
                                            },
                                        )}
                                        onClick={() => {
                                            if (opcaoDesabilitada) return
                                            field.onChange(option.value)
                                        }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={cn(
                                                    'size-5 rounded-full border-2 flex items-center justify-center',
                                                    {
                                                        'border-primary':
                                                            selecionado,
                                                        'border-border':
                                                            !selecionado,
                                                    },
                                                )}
                                            >
                                                {selecionado && (
                                                    <div className="size-3 rounded-full bg-primary" />
                                                )}
                                            </div>
                                            <h6 className="text-lg leading-5">
                                                {option.titulo}
                                            </h6>
                                        </div>
                                        <p className="text-xs text-secondary-foreground mt-2 ml-8">
                                            {option.descricao}
                                        </p>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>

                    {helperText && !error ? (
                        <span className="text-xs text-secondary-foreground">
                            {helperText}
                        </span>
                    ) : null}
                    {error ? (
                        <span className="text-xs text-error">
                            {error.message}
                        </span>
                    ) : null}
                </div>
            )}
        />
    )
}
